import type { RouterOutputs } from "@studygenie/api/trpc/routers/_app";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useMode } from "@/features/quiz/hooks/use-mode";
import { useTRPC } from "@/trpc/client";

type Props = {
  id: string;
  questionId: string;
};

// Assuming getQuestions is an async function, we use ReturnType and Awaited
type QuestionsData = RouterOutputs["questions"]["getWithFileNames"];
type QuestionsWithOptionsData = RouterOutputs["questions"]["getWithOptions"];

export const useDeleteQuestion = ({ id, questionId }: Props) => {
  const queryClient = useQueryClient();
  const trpc = useTRPC();
  const { mode } = useMode();

  return useMutation(
    trpc.questions.delete.mutationOptions({
      onSuccess: async () => {
        queryClient.setQueryData<QuestionsData>(
          trpc.questions.getWithFileNames.queryKey({ id }),
          (existingData) => {
            if (existingData && Array.isArray(existingData.questions)) {
              return {
                ...existingData,
                questions: existingData.questions.filter(
                  (question) => question.questions.id !== questionId,
                ),
              };
            }
            return existingData; // Return unchanged if structure is unexpected
          },
        );

        // Update QuestionsWithOptionsData
        queryClient.setQueryData<QuestionsWithOptionsData>(
          trpc.questions.getWithOptions.queryKey({ id, mode: mode.value }),
          (existingData) => {
            if (existingData && Array.isArray(existingData.questions)) {
              return {
                ...existingData,
                questions: existingData.questions.filter(
                  (question) => question.id !== questionId,
                ),
              };
            }
            return existingData; // Return unchanged if structure is unexpected
          },
        );

        queryClient.invalidateQueries({
          queryKey: trpc.progress.getStudySetProgress.queryKey({ id }),
        });
        toast.success("Question deleted successfully");
      },
      onError: (error) => {
        console.error("Failed to delete question", error);
        toast.error(error.message);
      },
    }),
  );
};
